// packages/components/src/agent_profile/showcase/OnboardingSimple.tsx
import { Card, Button } from '@dopomogai/ui';
import { Settings, Clock, ChevronRight, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

type Accent =
  | 'emerald'
  | 'teal'
  | 'amber'
  | 'sky'
  | 'violet'
  | 'indigo';

export type OnboardingStep = {
  title: string;
  description?: string;
  mediaUrl?: string;
  cta?: { text: string; onClick: () => void };
};

type Props = {
  title?: string;
  subtitle?: string;
  steps: OnboardingStep[];
  typicalSetupTimeText?: string; // e.g. "Typical setup: 5–10 days"
  className?: string;

  // New: accent header
  headerAccent?: Accent; // recommended: 'amber'
};

function accentToClasses(accent?: Accent) {
  switch (accent) {
    case 'amber':
      return { bg: 'bg-amber-50', text: 'text-amber-900', icon: 'text-amber-700' };
    case 'emerald':
      return { bg: 'bg-emerald-50', text: 'text-emerald-900', icon: 'text-emerald-700' };
    case 'teal':
      return { bg: 'bg-teal-50', text: 'text-teal-900', icon: 'text-teal-700' };
    case 'sky':
      return { bg: 'bg-sky-50', text: 'text-sky-900', icon: 'text-sky-700' };
    case 'violet':
      return { bg: 'bg-violet-50', text: 'text-violet-900', icon: 'text-violet-700' };
    case 'indigo':
      return { bg: 'bg-indigo-50', text: 'text-indigo-900', icon: 'text-indigo-700' };
    default:
      return { bg: 'bg-slate-50', text: 'text-slate-900', icon: 'text-slate-700' };
  }
}

export function OnboardingSimple({
  title = 'How we implement (done-for-you)',
  subtitle = 'Not SaaS — we tailor the agent to your team and tools.',
  steps,
  typicalSetupTimeText,
  className,
  headerAccent = 'amber',
}: Props) {
  if (!steps || steps.length === 0) return null;

  const c = accentToClasses(headerAccent);

  return (
    <Card className={className} noPadding>
      <div className={['px-5 py-4 border-b-2 border-black rounded-t-xl', c.bg, c.text].join(' ')}>
        <div className="flex items-center gap-2">
          <Settings size={22} className={c.icon} />
          <h3 className="text-xl font-extrabold leading-tight">{title}</h3>
        </div>
        <div className="text-sm mt-1 opacity-90">{subtitle}</div>
      </div>

      <div className="p-5">
        <div className="space-y-6 md:space-y-8">
          {typicalSetupTimeText && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-900 border-2 border-black rounded-xl px-3 py-2">
                <Clock size={16} />
                <span className="text-sm font-semibold">{typicalSetupTimeText}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={`${s.title}-${i}`}
                animate={INNER_CARD_ANIMATE_RESTING}
                whileHover={INNER_CARD_HOVER}
                transition={INNER_CARD_TRANSITION_HOVER}
                className="bg-white border-2 border-black rounded-xl p-4 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-amber-100 border-2 border-black text-amber-800 font-bold flex items-center justify-center">
                    {i + 1}
                  </div>
                  <h4 className="text-md font-semibold text-slate-800">{s.title}</h4>
                </div>
                {s.mediaUrl && (
                  <div className="mb-3 rounded-lg overflow-hidden border-2 border-black aspect-video bg-slate-100">
                    <img src={s.mediaUrl} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                )}
                {s.description && <p className="text-sm text-slate-600 flex-grow">{s.description}</p>}
                {s.cta && (
                  <Button
                    text={s.cta.text}
                    variant="secondary"
                    size="sm"
                    onClick={s.cta.onClick}
                    icon={<ChevronRight size={14} />}
                    iconPosition="right"
                    className="mt-3"
                  />
                )}
              </motion.div>
            ))}
          </div>

          <div className="pt-1 text-sm text-slate-600">
            We analyze your processes, connect your systems, tailor flows, train your team, and then iterate with our Meta agents to keep improving.
          </div>

          <div className="pt-1">
            <Button variant="ghost" text="See a sample onboarding plan" icon={<PlayCircle size={16} />} />
          </div>
        </div>
      </div>
    </Card>
  );
}

export default OnboardingSimple;
